import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoSparkles, IoChevronDown, IoChevronUp, IoRefresh } from 'react-icons/io5';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './BookSummary.css';

export default function BookSummary({ book }) {
  const [expanded, setExpanded] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!book || !book.chapters || book.chapters.length === 0) return null;

  const totalWords = book.chapters.reduce((sum, ch) => sum + (ch.content ? ch.content.split(/\s+/).length : 0), 0);
  const avgRating = book.ratings ? (() => {
    const vals = Object.values(book.ratings);
    return vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  })() : null;

  const genreLabels = { romance: 'une romance', fantasy: 'un récit de fantasy', thriller: 'un thriller', scifi: 'un récit de science-fiction', drama: 'un drame', horror: "un récit d'horreur", humor: 'un récit humoristique', poetry: 'un recueil de poésie', adventure: 'un récit d\'aventure', other: 'un récit' };
  const genreText = genreLabels[book.genre] || 'un récit';
  const chaptersText = book.chapters.length + ' chapitre' + (book.chapters.length > 1 ? 's' : '');
  const wordsText = totalWords > 1000 ? Math.round(totalWords / 1000) + 'k mots' : totalWords + ' mots';

  const generateAISummary = async () => {
    setLoading(true);
    setError('');

    try {
      // Vérifier si un résumé existe déjà en cache
      const cacheDoc = await getDoc(doc(db, 'bookSummaries', book.id));
      if (cacheDoc.exists() && cacheDoc.data().summary) {
        setAiSummary(cacheDoc.data().summary);
        setLoading(false);
        return;
      }

      // Récupérer la clé API depuis config
      const configDoc = await getDoc(doc(db, 'config', 'openai'));
      if (!configDoc.exists() || !configDoc.data().apiKey) {
        setError('Clé API non configurée. Configure-la dans Firestore : config/openai → apiKey');
        setLoading(false);
        return;
      }

      const apiKey = configDoc.data().apiKey;

      // Préparer le contenu (premiers 3000 caractères max pour limiter les tokens)
      const content = book.chapters.map(ch =>
        `${ch.title}: ${(ch.content || '').substring(0, 500)}`
      ).join('\n').substring(0, 3000);

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'Tu es un critique littéraire français. Génère un résumé engageant et captivant du livre en 3-4 phrases. Le résumé doit donner envie de lire sans spoiler les moments clés. Écris en français.'
            },
            {
              role: 'user',
              content: `Titre: ${book.title}\nAuteur: ${book.author}\nGenre: ${book.genre}\nSynopsis: ${book.synopsis || 'Non disponible'}\n\nExtraits des chapitres:\n${content}`
            }
          ],
          max_tokens: 250,
          temperature: 0.7,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur API: ' + response.status);
      }

      const data = await response.json();
      const summary = data.choices[0]?.message?.content || '';

      if (summary) {
        setAiSummary(summary);
        // Sauvegarder en cache
        await setDoc(doc(db, 'bookSummaries', book.id), {
          summary,
          bookTitle: book.title,
          generatedAt: new Date().toISOString(),
        });
      }
    } catch (e) {
      console.error('Erreur résumé IA:', e);
      setError('Impossible de générer le résumé. Réessaie plus tard.');
    }
    setLoading(false);
  };

  const handleExpand = () => {
    setExpanded(!expanded);
    if (!expanded && !aiSummary && !loading) {
      generateAISummary();
    }
  };

  return (
    <div className="summary-card">
      <motion.button
        className="summary-header"
        onClick={handleExpand}
        whileTap={{ scale: 0.98 }}
      >
        <div className="summary-header-left">
          <IoSparkles className="summary-icon" />
          <span className="summary-label">Résumé IA</span>
          <span className="summary-badge">✨ ChatGPT</span>
        </div>
        {expanded ? <IoChevronUp /> : <IoChevronDown />}
      </motion.button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="summary-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Infos de base */}
            <p className="summary-meta">
              <strong>{book.title}</strong> est {genreText} de {book.author}, composé de {chaptersText} ({wordsText}).
            </p>

            {/* Résumé IA */}
            {loading && (
              <div className="summary-loading">
                <div className="summary-loading-dots">
                  <span></span><span></span><span></span>
                </div>
                <p>Génération du résumé en cours...</p>
              </div>
            )}

            {error && (
              <div className="summary-error">
                <p>{error}</p>
                <button className="summary-retry" onClick={generateAISummary}>
                  <IoRefresh /> Réessayer
                </button>
              </div>
            )}

            {aiSummary && (
              <div className="summary-ai">
                <p className="summary-ai-text">{aiSummary}</p>
                <button className="summary-regenerate" onClick={() => { setAiSummary(null); generateAISummary(); }}>
                  <IoRefresh /> Régénérer
                </button>
              </div>
            )}

            {!aiSummary && !loading && !error && (
              <p className="summary-text">{book.synopsis || 'Aucun synopsis disponible.'}</p>
            )}

            <div className="summary-stats">
              <span>❤️ {(book.likes || 0)} likes</span>
              <span>👁 {(book.views || 0)} vues</span>
              {avgRating && <span>⭐ {avgRating}/5</span>}
              {book.tags && book.tags.length > 0 && <span>🏷 {book.tags.join(', ')}</span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}